import Entity from "_framework/entities/entity";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import DocumentSchema, { DocumentJSON, DocumentParams } from "documents/_schema/document-schema";
import DocumentName from "documents/names/document-name";
import DocumentTopic from "documents/topics/document-topic";
import { DocumentFileType } from "documents/types/document-file-type";
import { JSONType } from "work/proposals/redlining/field-redlines/field-redline";

export default class Document extends Entity<Document, DocumentJSON, DocumentParams> {
  public static create(values: DocumentParams): Document {
    const parsed = DocumentSchema.validate(values);
    return new Document(parsed);
  }

  public static fromJSON(json: unknown): Document {
    if (!DocumentSchema.isValidJSON(json)) {
      throw new InvalidDocumentJSONError(json as JSONType, "JSON does not conform to DocumentSchema.");
    }
    const parsed = DocumentSchema.deserialize(json);
    return new Document(parsed);
  }

  public static fromJSONArray(jsonArray: DocumentJSON[]): Document[] {
    return jsonArray.map((json) => Document.fromJSON(json));
  }

  public readonly id: Guid;
  public readonly creatorId?: Guid; // TODO: Convert to EntityMember type (needs to happen on the backend first)
  public readonly created?: AHDate;
  public readonly name: DocumentName = new DocumentName("Untitled Document");
  public readonly fileSize?: number; // TODO: Move into File type
  public readonly fileType: DocumentFileType; // TODO: Move into File type
  public readonly readAccess?: Guid[];
  public readonly writeAccess?: Guid[];
  public readonly hasSystemPermissions: boolean = false;
  public readonly replacesId?: Guid;
  // TODO: Missing "updated: AHDate" field.

  public get topics(): DocumentTopic[] {
    return [...this._topics];
  }

  public get templateIds(): Guid[] {
    return [...this._templateIds];
  }

  public get string(): string {
    return this.toString();
  }

  public get isTemplate(): boolean {
    return this.topics.some((topic) => topic.context?.toLowerCase().includes("template"));
  }

  public toJSON(): DocumentJSON {
    return DocumentSchema.serialize(this);
  }

  public valueOf(): DocumentJSON {
    return this.toJSON();
  }

  public toString(): string {
    return `Document: ${this.name?.toString()} (ID: ${this.id.value})`;
  }

  public isEqualTo(other: Document | null | undefined): boolean {
    return Boolean(
      other &&
        this.is(other) &&
        (this.name?.isEqualTo(other.name) ?? (!this.name && !other.name)) &&
        this.creatorId?.isEqualTo(other.creatorId) &&
        this.fileSize === other.fileSize &&
        this.fileType === other.fileType &&
        this.topics.length === other.topics.length &&
        this.topics.every((topic) => other.topics.some((otherTopic) => topic.isEqualTo(otherTopic))) &&
        this.templateIds.length === other.templateIds.length &&
        this.templateIds.every((id) => other.templateIds.some((otherId) => id.isEqualTo(otherId))) &&
        this.readAccess?.length === other.readAccess?.length &&
        this.readAccess?.every((id) => other.readAccess?.some((otherId) => id.isEqualTo(otherId))) &&
        this.writeAccess?.length === other.writeAccess?.length &&
        this.writeAccess?.every((id) => other.writeAccess?.some((otherId) => id.isEqualTo(otherId)))
    );
  }

  public isUsingTemplate = (templateId: Guid): boolean => this.templateIds.some((id) => id.isEqualTo(templateId));

  public is = (document: Document | null | undefined): boolean => !!document && this.id.isEqualTo(document.id);

  private readonly _topics: DocumentTopic[];
  private readonly _templateIds: Guid[] = [];

  protected constructor(params: DocumentParams) {
    super(params);
    this.id = params.id;
    this.creatorId = params.creatorId;
    this.fileSize = params.fileSize;
    this.fileType = params.fileType;
    this.created = params.created;
    this.readAccess = params.readAccess;
    this.writeAccess = params.writeAccess;
    this.hasSystemPermissions = params.hasSystemPermissions ?? false;
    this.name = params.name ?? new DocumentName("Untitled Document");
    this._topics = params.topics?.map((topic) => DocumentTopic.create(topic)) ?? [];
    this._templateIds = params.templateIds ?? [];
    this.replacesId = params.replacesId;
  }
}

export class InvalidDocumentJSONError extends Error {
  constructor(public readonly json: JSONType, message?: string) {
    super("Invalid Document JSON" + (message ? `: ${message}` : "."));
  }
}
